import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  TextField,
  Stack,
  Typography,
  Autocomplete,
  createFilterOptions,
  Chip,
} from '@mui/material';
import type {
  ProductMapping,
  ProductsListItem,
  VendorProduct,
} from '@techgitter/contracts';
import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import { createProductMapping, updateProductMapping } from '../../services/productMappingService';
import { getVendorProducts } from '../../services/vendorProductService';
import { fetchInventory } from '../../services/inventoryApi';
import { LICENSE_CATEGORIES, getLicenseCategoryForSku } from '../../data/licenseCategories';
import './reconciliationPanel.css';

// Distinct SKU prefixes that identify a license product (e.g. BML, BGL).
const LICENSE_SKU_PREFIXES = Array.from(
  new Set(LICENSE_CATEGORIES.flatMap((c) => c.skuPrefixes)),
);

// Explicit substring filter on name + sku (mirrors CreateVendorProductDialog;
// the default can drift to the verbose description). Trimmed + capped so the
// hundreds-of-licenses list stays responsive.
const filterProducts = createFilterOptions<ProductsListItem>({
  trim: true,
  limit: 50,
  stringify: (o) => `${o.displayName ?? ''} ${o.sku ?? ''}`,
});
const filterVendorProducts = createFilterOptions<VendorProduct>({
  trim: true,
  limit: 50,
  stringify: (o) => `${o.name ?? ''} ${o.sku ?? ''} ${o.vendorName ?? ''}`,
});

/** Parse the term token off a commercial SKU (`...Annual` / `...Monthly`). */
function commercialTerm(sku?: string | null): 'monthly' | 'annual' | null {
  if (!sku) return null;
  const tokens = sku.split('.').map((t) => t.trim().toLowerCase());
  if (tokens.includes('annual')) return 'annual';
  if (tokens.includes('monthly')) return 'monthly';
  return null;
}

interface Props {
  open: boolean;
  /** When set, the dialog edits this mapping; otherwise it creates a new one. */
  mapping?: ProductMapping | null;
  /**
   * Pre-selected vendor product for create mode — used when mapping an unmapped
   * vendor product from the needs-attention list. Ignored when editing.
   */
  initialVendorProduct?: VendorProduct | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Create or edit a `cr001_product_mappings` row — the commercial ⇄ vendor
 * product bridge that feeds billing coverage (Control 2). Reuses the hardened
 * commercial-product typeahead and adds a vendor-product picker. Surfaces a
 * live term-mismatch warning (warn-only) so an annual SKU mapped to a monthly
 * vendor product is caught before saving.
 */
export const ProductMappingDialog: React.FC<Props> = ({
  open,
  mapping,
  initialVendorProduct,
  onClose,
  onSaved,
}) => {
  const isEdit = !!mapping;

  const [products, setProducts] = useState<ProductsListItem[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductsListItem | null>(null);

  const [vendorProducts, setVendorProducts] = useState<VendorProduct[]>([]);
  const [vendorProductsLoading, setVendorProductsLoading] = useState(false);
  const [selectedVendorProduct, setSelectedVendorProduct] = useState<VendorProduct | null>(null);

  const [seatMultiplier, setSeatMultiplier] = useState(1);
  const [isActive, setIsActive] = useState(true);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when (re)opened.
  useEffect(() => {
    if (!open) return;
    setSeatMultiplier(mapping?.seatMultiplier ?? 1);
    setIsActive(mapping?.isActive ?? true);
    setSelectedProduct(null);
    // In create mode, honour a pre-selected vendor product (mapping an unmapped
    // one from the needs-attention list). Editing pre-selects from the row.
    setSelectedVendorProduct(mapping ? null : initialVendorProduct ?? null);
    setShowAllCategories(false);
    setError(null);
  }, [open, mapping, initialVendorProduct]);

  // Load commercial products. Same approach as CreateVendorProductDialog: the
  // catalog can exceed the API page cap, so license-only mode fetches each SKU
  // prefix server-side and merges; "show all" falls back to a single page.
  useEffect(() => {
    if (!open) return;
    let active = true;
    const controller = new AbortController();
    const signal = controller.signal;
    setProductsLoading(true);

    const load = async (): Promise<ProductsListItem[]> => {
      if (showAllCategories) {
        const res = await fetchInventory({ page: 1, pageSize: 200, sort: 'displayName', order: 'asc', signal });
        return res.items;
      }
      const batches = await Promise.all(
        LICENSE_SKU_PREFIXES.map((prefix) =>
          fetchInventory({ page: 1, pageSize: 200, search: prefix, sort: 'displayName', order: 'asc', signal }).then(
            (r) => r.items,
          ),
        ),
      );
      const byId = new Map<string, ProductsListItem>();
      batches.flat().forEach((it) => byId.set(it.id, it));
      return Array.from(byId.values());
    };

    load()
      .then((items) => {
        if (active) setProducts(items);
      })
      .catch((e) => {
        if (!active || (e instanceof Error && e.name === 'AbortError')) return;
        setError('Could not load the commercial product list.');
      })
      .finally(() => {
        if (active) setProductsLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [open, showAllCategories]);

  // Load all vendor products (across suppliers) for the picker.
  useEffect(() => {
    if (!open) return;
    let active = true;
    const controller = new AbortController();
    setVendorProductsLoading(true);
    getVendorProducts(undefined, controller.signal)
      .then((items) => {
        if (active) setVendorProducts(items);
      })
      .catch((e) => {
        if (!active || (e instanceof Error && e.name === 'AbortError')) return;
        setError('Could not load the vendor product list.');
      })
      .finally(() => {
        if (active) setVendorProductsLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [open]);

  // Pre-select the edited mapping's products once their lists have loaded.
  useEffect(() => {
    if (!isEdit || !mapping) return;
    if (!selectedProduct && products.length) {
      const found = products.find((p) => p.id === mapping.productId);
      if (found) setSelectedProduct(found);
    }
    if (!selectedVendorProduct && vendorProducts.length) {
      const found = vendorProducts.find((v) => v.id === mapping.vendorProductId);
      if (found) setSelectedVendorProduct(found);
    }
  }, [isEdit, mapping, products, vendorProducts, selectedProduct, selectedVendorProduct]);

  const productOptions = useMemo(
    () =>
      products
        .filter((p) => !p.disabled)
        .filter((p) => showAllCategories || getLicenseCategoryForSku(p.sku) !== null)
        .sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? '')),
    [products, showAllCategories],
  );

  const vendorProductOptions = useMemo(
    () =>
      [...vendorProducts]
        .filter((v) => v.isActive)
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    [vendorProducts],
  );

  // Live term-mismatch warning (warn-only) — uses the edited mapping's hydrated
  // SKU when the commercial product list hasn't been browsed yet.
  const termWarning = useMemo(() => {
    const sku = selectedProduct?.sku ?? mapping?.productSku ?? null;
    const cTerm = commercialTerm(sku);
    const vTerm = selectedVendorProduct?.billingTerm ?? mapping?.vendorProductBillingTerm ?? null;
    if (cTerm && vTerm && cTerm !== vTerm) {
      return `Term mismatch: commercial SKU is ${cTerm} but the vendor product is ${vTerm}. Save only if this cross-term mapping is intentional.`;
    }
    return null;
  }, [selectedProduct, selectedVendorProduct, mapping]);

  const submit = async () => {
    if (!selectedProduct) {
      setError('Pick a commercial product.');
      return;
    }
    if (!selectedVendorProduct) {
      setError('Pick a vendor product.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const multiplier = Math.max(1, Number(seatMultiplier) || 1);
      if (isEdit && mapping) {
        await updateProductMapping(mapping.id, {
          productId: selectedProduct.id,
          vendorProductId: selectedVendorProduct.id,
          seatMultiplier: multiplier,
          isActive,
        });
      } else {
        await createProductMapping({
          productId: selectedProduct.id,
          vendorProductId: selectedVendorProduct.id,
          seatMultiplier: multiplier,
          isActive,
        });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const dialogTitle = isEdit ? 'Edit product mapping' : 'Create product mapping';

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-label={dialogTitle}
      PaperProps={{
        sx: { background: 'transparent', boxShadow: 'none', borderRadius: 0, overflow: 'visible' },
      }}
    >
      <SidePanel
        title={dialogTitle}
        submitLabel={saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
        isValid={!saving && !!selectedProduct && !!selectedVendorProduct}
        onClose={() => {
          if (!saving) onClose();
        }}
        onSubmit={submit}
      >
        {error && <div className="side-panel__alert side-panel__alert--error">{error}</div>}
        {termWarning && <div className="side-panel__alert side-panel__alert--warning">{termWarning}</div>}

        <p className="recon-dialog__hint">
          Links a commercial product (what we sell &amp; bill) to a vendor product
          (the supplier SKU) so it feeds billing coverage.
        </p>

        <Autocomplete
          className="recon-dialog__autocomplete"
          options={productOptions}
          loading={productsLoading}
          value={selectedProduct}
          onChange={(_, v) => setSelectedProduct(v)}
          getOptionLabel={(o) => o.displayName || o.sku || ''}
          getOptionKey={(o) => o.id}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          filterOptions={filterProducts}
          renderOption={(props, o) => {
            const { key, ...liProps } = props as { key?: string };
            return (
              <li {...liProps} key={o.id}>
                <Stack>
                  <span>{o.displayName || o.sku}</span>
                  {o.sku && o.sku !== o.displayName && (
                    <Typography variant="caption" color="text.secondary">
                      {o.sku}
                    </Typography>
                  )}
                </Stack>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label="Commercial product" size="small" required />
          )}
        />
        <label className="side-panel__checkbox">
          <input
            type="checkbox"
            checked={showAllCategories}
            onChange={(e) => {
              setShowAllCategories(e.target.checked);
              setSelectedProduct(null);
            }}
          />
          Show all product categories
        </label>
        <span className="recon-dialog__help">
          {showAllCategories
            ? 'Showing all active products.'
            : 'Showing active licenses/subscriptions only.'}
        </span>

        <Autocomplete
          className="recon-dialog__autocomplete"
          options={vendorProductOptions}
          loading={vendorProductsLoading}
          value={selectedVendorProduct}
          onChange={(_, v) => setSelectedVendorProduct(v)}
          getOptionLabel={(o) => o.name || o.sku || ''}
          getOptionKey={(o) => o.id}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          filterOptions={filterVendorProducts}
          renderOption={(props, o) => {
            const { key, ...liProps } = props as { key?: string };
            return (
              <li {...liProps} key={o.id}>
                <Stack sx={{ width: '100%' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{o.name || o.sku}</span>
                    <Chip size="small" label={o.billingTerm} variant="outlined" />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {o.sku}
                    {o.vendorName ? ` · ${o.vendorName}` : ''}
                  </Typography>
                </Stack>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label="Vendor product" size="small" required />
          )}
        />

        <FormField label="Seat multiplier">
          <input
            type="number"
            min={1}
            value={seatMultiplier}
            onChange={(e) => setSeatMultiplier(Number(e.target.value))}
          />
        </FormField>
        <span className="recon-dialog__help">
          Vendor seats expected per commercial seat (default 1).
        </span>

        <label className="side-panel__checkbox">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Active
        </label>
      </SidePanel>
    </Dialog>
  );
};
